import React, { useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import DialogHeader from "../../../components/Dialog/DialogHeader";
import UserDialog from "../../../components/Dialog/UserDialog";
import {
    CreateFighterTypeDto,
    FIGHTER_TYPE_ENUM_LABELS,
    FighterTypeCharacteristics,
    FighterTypeDict,
} from "../../../model/Dto/Dict/FighterType";
import { GetAllFactionsResult } from "../../../request/api/faction/getAllFactions";

interface FighterTypeDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: CreateFighterTypeDto) => Promise<boolean>;
    initialData?: FighterTypeDict | null;
    title: string;
    factions: GetAllFactionsResult;
}

type CharInputs = Record<keyof FighterTypeCharacteristics, string>;

const CHAR_LABELS: { key: keyof FighterTypeCharacteristics; label: string }[] = [
    { key: "m", label: "M" },
    { key: "ws", label: "WS" },
    { key: "bs", label: "BS" },
    { key: "s", label: "S" },
    { key: "t", label: "T" },
    { key: "w", label: "W" },
    { key: "i", label: "I" },
    { key: "a", label: "A" },
    { key: "ld", label: "Ld" },
    { key: "cl", label: "Cl" },
    { key: "wp", label: "Wp" },
    { key: "int", label: "Int" },
];

const emptyChars = (): CharInputs =>
    Object.fromEntries(CHAR_LABELS.map((c) => [c.key, "0"])) as CharInputs;

const charsToInputs = (ch: FighterTypeCharacteristics): CharInputs =>
    Object.fromEntries(CHAR_LABELS.map((c) => [c.key, String(ch[c.key])])) as CharInputs;

const parseChars = (inputs: CharInputs): FighterTypeCharacteristics =>
    Object.fromEntries(
        CHAR_LABELS.map((c) => [c.key, parseInt(inputs[c.key]) || 0])
    ) as unknown as FighterTypeCharacteristics;

export default function FighterTypeDialog({
    open,
    onClose,
    onSave,
    initialData,
    title,
    factions,
}: FighterTypeDialogProps) {
    const [name, setName] = useState("");
    const [cost, setCost] = useState("0");
    const [startingLevel, setStartingLevel] = useState("1");
    const [factionValue, setFactionValue] = useState<string>("__none__");
    const [fighterTypeEnum, setFighterTypeEnum] = useState<number>(1);
    const [isBase, setIsBase] = useState(false);
    const [canEquipStandardWargear, setCanEquipStandardWargear] = useState(false);
    const [charInputs, setCharInputs] = useState<CharInputs>(emptyChars);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (initialData) {
            setName(initialData.name);
            setCost(String(initialData.cost));
            setStartingLevel(String(initialData.startingLevel));
            setFactionValue(initialData.factionId ?? "__none__");
            setFighterTypeEnum(initialData.fighterTypeEnum);
            setIsBase(initialData.isBase);
            setCanEquipStandardWargear(initialData.canEquipStandardWargear);
            setCharInputs(charsToInputs(initialData.characteristics));
        } else {
            setName("");
            setCost("0");
            setStartingLevel("1");
            setFactionValue("__none__");
            setFighterTypeEnum(1);
            setIsBase(false);
            setCanEquipStandardWargear(false);
            setCharInputs(emptyChars());
        }
    }, [initialData, open]);

    const handleSave = async () => {
        setLoading(true);
        const success = await onSave({
            name: name.trim(),
            cost: parseInt(cost) || 0,
            factionId: factionValue === "__none__" ? null : factionValue,
            fighterTypeEnum,
            isBase,
            canEquipStandardWargear,
            startingLevel: parseInt(startingLevel) || 1,
            characteristics: parseChars(charInputs),
        });
        setLoading(false);
        if (success) onClose();
    };

    return (
        <UserDialog handleClose={onClose} open={open} maxWidth="md">
            <DialogHeader loading={loading} title={title} />
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoFocus
                                label="Название"
                                fullWidth
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Стоимость"
                                fullWidth
                                inputProps={{ inputMode: "numeric" }}
                                value={cost}
                                onChange={(e) => setCost(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Стартовый уровень"
                                fullWidth
                                inputProps={{ inputMode: "numeric" }}
                                value={startingLevel}
                                onChange={(e) => setStartingLevel(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth disabled={loading}>
                                <InputLabel>Фракция</InputLabel>
                                <Select
                                    value={factionValue}
                                    label="Фракция"
                                    onChange={(e) => setFactionValue(e.target.value)}
                                >
                                    <MenuItem value="__none__">Общедоступный</MenuItem>
                                    {factions.map((f) => (
                                        <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth disabled={loading}>
                                <InputLabel>Тип бойца</InputLabel>
                                <Select
                                    value={fighterTypeEnum}
                                    label="Тип бойца"
                                    onChange={(e) => setFighterTypeEnum(Number(e.target.value))}
                                >
                                    {Object.entries(FIGHTER_TYPE_ENUM_LABELS).map(([val, label]) => (
                                        <MenuItem key={val} value={Number(val)}>{label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isBase}
                                        onChange={(e) => setIsBase(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Базовый"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={canEquipStandardWargear}
                                        onChange={(e) => setCanEquipStandardWargear(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Стандартное снаряжение"
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" color="text.secondary">
                        Характеристики
                    </Typography>
                    <Grid container spacing={1}>
                        {CHAR_LABELS.map(({ key, label }) => (
                            <Grid item xs={2} key={key}>
                                <TextField
                                    label={label}
                                    fullWidth
                                    size="small"
                                    inputProps={{ inputMode: "numeric" }}
                                    value={charInputs[key]}
                                    onChange={(e) =>
                                        setCharInputs((prev) => ({ ...prev, [key]: e.target.value }))
                                    }
                                    disabled={loading}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Отмена</Button>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {loading ? "Сохранение..." : "Сохранить"}
                </Button>
            </DialogActions>
        </UserDialog>
    );
}
